import React from 'react';

// =============================================================================
// NOTIFICATION SYSTEM
// =============================================================================

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: number;
  message: string;
  type: NotificationType;
}

/**
 * Custom hook for managing notifications
 */
export const useNotifications = () => {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);

  const addNotification = React.useCallback((message: string, type: NotificationType = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  const removeNotification = React.useCallback((id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = React.useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications
  };
};

/**
 * Enhanced notification item with auto-dismiss and close button
 */
export const NotificationItem = ({ 
  notification, 
  onDismiss 
}: { 
  notification: Notification; 
  onDismiss: (id: number) => void;
}) => {
  const typeClasses = {
    success: 'bg-green-600 border-green-500',
    error: 'bg-red-600 border-red-500',
    warning: 'bg-yellow-600 border-yellow-500',
    info: 'bg-blue-600 border-blue-500'
  };
  
  return (
    <div className={`${typeClasses[notification.type]} text-white px-4 py-2 rounded-lg shadow-lg border flex items-center justify-between max-w-sm`}>
      <span className="text-sm">{notification.message}</span>
      <button 
        onClick={() => onDismiss(notification.id)}
        className="ml-2 text-white hover:text-gray-200 font-bold"
      >
        ×
      </button>
    </div>
  );
};

/**
 * Notifications container with positioning
 */
export const NotificationsContainer = ({ 
  notifications, 
  onDismiss 
}: { 
  notifications: Notification[]; 
  onDismiss: (id: number) => void;
}) => (
  <div className="fixed top-4 right-4 space-y-2 z-50 max-w-sm">
    {notifications.map((notification) => (
      <NotificationItem 
        key={notification.id} 
        notification={notification} 
        onDismiss={onDismiss} 
      />
    ))}
  </div>
);

/**
 * Mobile notifications display (different positioning)
 */
export const MobileNotificationsDisplay = ({ 
  notifications 
}: { 
  notifications: Notification[];
}) => (
  <>
    {notifications.length > 0 && (
      <div className="mb-4 space-y-2">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`p-3 rounded-lg text-white text-sm ${
              notification.type === 'error' ? 'bg-red-600' :
              notification.type === 'success' ? 'bg-green-600' :
              notification.type === 'warning' ? 'bg-yellow-600' :
              'bg-blue-600'
            }`}
          >
            {notification.message}
          </div>
        ))}
      </div>
    )}
  </>
);

// =============================================================================
// STATISTICS AND SCORING
// =============================================================================

/**
 * Calculate player score for leaderboard (profit/loss)
 */
export const calculatePlayerScore = (player: any): number => {
  const totalBuyIns = player.totalBuyIns || (1000 * (1 + (player.rebuyCount || 0)));
  return player.chips - totalBuyIns;
};

/**
 * Enhanced player data with calculated statistics
 */
export const enhancePlayerWithStats = (player: any) => ({
  ...player,
  calculatedScore: calculatePlayerScore(player),
  totalBuyIns: player.totalBuyIns || (1000 * (1 + (player.rebuyCount || 0))),
  profitLoss: calculatePlayerScore(player),
  rebuyCount: player.rebuyCount || 0
});

/**
 * Sort players for leaderboard display
 */
export const sortPlayersForLeaderboard = (players: any[]) => {
  return players
    .map(enhancePlayerWithStats)
    .sort((a, b) => b.calculatedScore - a.calculatedScore);
};

/**
 * Get leaderboard position styling based on rank
 */
export const getLeaderboardPositionStyle = (index: number): string => {
  return index === 0 ? 'bg-yellow-500' : 
         index === 1 ? 'bg-gray-400' : 
         index === 2 ? 'bg-orange-500' : 'bg-gray-600';
};

/**
 * Get player status badge info
 */
export const getPlayerStatusBadge = (player: any) => {
  if (player.isFolded) return { text: 'Folded', className: 'bg-red-600' };
  if (player.isAllIn) return { text: 'All-In', className: 'bg-yellow-600' };
  if (!player.isConnected) return { text: 'Offline', className: 'bg-gray-600' };
  return { text: 'Active', className: 'bg-green-600' };
};

// =============================================================================
// LEADERBOARD COMPONENTS
// =============================================================================

/**
 * Desktop leaderboard display component
 */
export const DesktopLeaderboard = ({ 
  players, 
  myPlayerId, 
  onClose 
}: {
  players: any[];
  myPlayerId: string;
  onClose: () => void;
}) => {
  const sortedPlayers = sortPlayersForLeaderboard(players);

  return (
    <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 rounded-lg p-6 mb-6 shadow-2xl border border-yellow-400">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-white text-2xl font-bold flex items-center">
          <span className="mr-2">🏆</span> LEADERBOARD
        </h2>
        <button 
          onClick={onClose}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg font-semibold transition-all"
        >
          ✕ Close
        </button>
      </div>
      
      <div className="bg-white bg-opacity-10 rounded-lg p-4">
        <div className="space-y-3">
          {sortedPlayers.map((player, index) => (
            <div 
              key={player.id} 
              className={`bg-white bg-opacity-10 rounded p-3 flex justify-between items-center ${
                player.id === myPlayerId ? 'ring-2 ring-blue-400' : ''
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${getLeaderboardPositionStyle(index)}`}>
                  {index + 1}
                </div>
                <div>
                  <div className="text-white font-semibold">
                    {player.name}
                    {player.id === myPlayerId && <span className="ml-2 text-blue-300 text-xs">(You)</span>}
                  </div>
                  <div className={`text-xl font-bold ${player.calculatedScore >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {player.calculatedScore >= 0 ? '+' : ''}${player.calculatedScore}
                  </div>
                  <div className="text-gray-300 text-xs">
                    ${player.chips} chips {player.rebuyCount > 0 && `• ${player.rebuyCount} rebuy${player.rebuyCount !== 1 ? 's' : ''}`}
                  </div>
                  {player.rebuyCount > 0 && (
                    <div className="text-yellow-400 text-xs">
                      {player.rebuyCount} rebuy{player.rebuyCount > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <LeaderboardPlayerBadge player={player} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile leaderboard display component
 */
export const MobileLeaderboard = ({ 
  players, 
  myPlayerId, 
  onClose 
}: {
  players: any[];
  myPlayerId: string;
  onClose: () => void;
}) => {
  const sortedPlayers = sortPlayersForLeaderboard(players);

  return (
    <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-white text-lg font-bold flex items-center">
          <span className="mr-2">🏆</span> LEADERBOARD
        </h2>
        <button 
          onClick={onClose}
          className="bg-white bg-opacity-20 text-white px-3 py-1 rounded text-sm"
        >
          ✕
        </button>
      </div>
      
      <div className="space-y-2">
        {sortedPlayers.map((player, index) => (
          <div 
            key={player.id} 
            className={`bg-white bg-opacity-20 rounded p-2 flex justify-between items-center ${
              player.id === myPlayerId ? 'ring-1 ring-blue-400' : ''
            }`}
          >
            <div className="flex items-center space-x-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${getLeaderboardPositionStyle(index)}`}>
                {index + 1}
              </div>
              <div>
                <div className="text-white text-sm font-medium">
                  {player.name}
                  {player.id === myPlayerId && <span className="ml-1 text-blue-300 text-xs">(You)</span>}
                </div>
                <div className={`font-bold ${player.calculatedScore >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {player.calculatedScore >= 0 ? '+' : ''}${player.calculatedScore}
                </div>
                <div className="text-gray-300 text-xs">
                  ${player.chips} chips
                </div>
              </div>
            </div>
            <div className="text-xs">
              <MobileLeaderboardPlayerBadge player={player} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Player badge component for leaderboard
 */
export const LeaderboardPlayerBadge = ({ player }: { player: any }) => {
  const badge = getPlayerStatusBadge(player);
  return <span className={`px-2 py-1 ${badge.className} text-white text-xs rounded`}>{badge.text}</span>;
};

/**
 * Mobile player badge component for leaderboard
 */
export const MobileLeaderboardPlayerBadge = ({ player }: { player: any }) => {
  const badge = getPlayerStatusBadge(player);
  return <span className={`px-1 py-1 ${badge.className} text-white rounded`}>{badge.text}</span>;
};

// =============================================================================
// STATISTICS VIEW COMPONENTS
// =============================================================================

/**
 * Game statistics view for mobile
 */
export const GameStatistics = ({ 
  players, 
  gameState, 
  myPlayerId 
}: {
  players: any[];
  gameState: any;
  myPlayerId: string;
}) => {
  const currentPlayer = players.find(p => p.id === myPlayerId);
  const sortedPlayers = sortPlayersForLeaderboard(players);
  const totalPot = gameState?.pot || 0;
  const activePlayers = players.filter(p => !p.isFolded && p.isConnected);
  
  return (
    <div className="space-y-4">
      {/* Personal Stats */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-3">Your Statistics</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-400">Current Chips:</span>
            <div className="text-blue-400 font-bold">${currentPlayer?.chips || 0}</div>
          </div>
          <div>
            <span className="text-gray-400">Current Bet:</span>
            <div className="text-purple-400 font-bold">${currentPlayer?.currentBet || 0}</div>
          </div>
          <div>
            <span className="text-gray-400">Profit/Loss:</span>
            <div className={`font-bold ${calculatePlayerScore(currentPlayer || {}) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {calculatePlayerScore(currentPlayer || {}) >= 0 ? '+' : ''}${calculatePlayerScore(currentPlayer || {})}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Rebuys:</span>
            <div className="text-yellow-400 font-bold">{currentPlayer?.rebuyCount || 0}/5</div>
          </div>
        </div>
      </div>

      {/* Game Stats */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-3">Game Statistics</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-400">Total Pot:</span>
            <div className="text-green-400 font-bold">${totalPot}</div>
          </div>
          <div>
            <span className="text-gray-400">Active Players:</span>
            <div className="text-blue-400 font-bold">{activePlayers.length}</div>
          </div>
          <div>
            <span className="text-gray-400">Round:</span>
            <div className="text-yellow-400 font-bold">{gameState?.round || 'Waiting'}</div>
          </div>
          <div>
            <span className="text-gray-400">Min Raise:</span>
            <div className="text-purple-400 font-bold">${gameState?.minRaise || 0}</div>
          </div>
        </div>
      </div>

      {/* Top Players */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-3">Top Players</h3>
        <div className="space-y-2">
          {sortedPlayers.slice(0, 3).map((player, index) => (
            <div key={player.id} className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${getLeaderboardPositionStyle(index)}`}>
                  {index + 1}
                </div>
                <span className="text-white text-sm">
                  {player.name}
                  {player.id === myPlayerId && <span className="text-blue-300 ml-1">(You)</span>}
                </span>
              </div>
              <div className={`font-bold text-sm ${player.calculatedScore >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {player.calculatedScore >= 0 ? '+' : ''}${player.calculatedScore}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format score with appropriate sign and color
 */
export const formatScore = (score: number): { text: string; className: string } => {
  return {
    text: `${score >= 0 ? '+' : ''}$${score}`,
    className: score >= 0 ? 'text-green-400' : 'text-red-400'
  };
};

/**
 * Get game phase description
 */
export const getGamePhaseDescription = (gameState: any): string => {
  if (!gameState) return 'Waiting for game to start';
  
  const phaseDescriptions = {
    'preflop': 'Pre-flop betting',
    'flop': 'Flop - 3 community cards',
    'turn': 'Turn - 4th community card',
    'river': 'River - 5th community card',
    'showdown': 'Showdown - revealing hands'
  };
  
  return phaseDescriptions[gameState.round] || `${gameState.round} phase`;
};

/**
 * Calculate win rate (if historical data available)
 */
export const calculateWinRate = (wins: number, totalGames: number): number => {
  if (totalGames === 0) return 0;
  return Math.round((wins / totalGames) * 100);
};